'use strict';

import { config, constants, links, regex, setDomain } from './config';
import * as Helper from './helper';
import { resolveTargets } from './targets';
import { setTrackers, trackEvent } from './tracker';
import { dispatchEvent } from './events';

export default class Store {
  constructor() {
    this.data = {};
    this.viewer_id = constants.TEMP_VIEWER_ID;
    this.session_id = Helper.getGUID();
    this.getUser();
    this.eventsHeldForId = [];
  }

  setId(wtUUID) {
    this.viewer_id = wtUUID;
    this.setUser();
    // console.log(this.eventsHeldForId, this.session_id)
    this.eventsHeldForId.forEach((evt) => {
      let eventObj = JSON.parse(
        '{"' +
          decodeURI(evt)
            .replace(/"/g, '\\"')
            .replace(/&/g, '","')
            .replace(/=/g, '":"') +
          '"}'
      );
      eventObj.session_id = this.session_id;
      eventObj.viewer_id = this.user && this.user.id;
      const event = Helper.parseJsonToFormData(eventObj);
      this.fireEventApiCall(event);
    });
    this.eventsHeldForId = [];
  }

  initialize(bucketId, videoId, json, forceStart) {
    this.data = {};
    this.data.isMobileDevice = Helper.mobileCheck();
    this.data.isTabletDevice = Helper.tabletCheck();
    this.data.isFacebookApp = Helper.isFacebookApp();
    this.data.orientation = Helper.getOrientation();

    if (!bucketId || !videoId) {
      let videoString = window.location.pathname.match(regex.extractVideoID);

      if (videoString) {
        this.data.bucketId = videoString[0].substr(0, 16);
        this.data.videoId = videoString[0].substr(16);
      } else {
        this.data.bucketId = config.DEFAULT_BUCKET_ID;
        this.data.videoId = config.DEFAULT_VIDEO_ID;
      }
    } else {
      this.data.bucketId = bucketId;
      this.data.videoId = videoId;
      this.data.json = json;
    }

    this.data.id = 'wtPlay_' + this.data.videoId;

    return new Promise((resolve, reject) => {
      if (!json) {
        if (forceStart) {
          let currentScriptUrl = document.currentScript;
          let url = new URL(currentScriptUrl && currentScriptUrl.src);
          if (url) {
            let origin =
              window.location.host.indexOf('localhost') !== -1
                ? config.STAGING_DOMAIN
                : url.origin;
            const metaTag = document.querySelector(
              'meta[name="wootag:origin_domain"'
            );
            const metaOrigin = metaTag && metaTag.getAttribute('content');
            origin = metaOrigin || origin;
            setDomain(origin);
          } else {
            setDomain(config.PREPROD_DOMAIN);
          }
        }

        this.fetchUserJSON().then((json) => {
          const addon = json && json.addon;
          if (addon) {
            const branding = addon.find((item) => item.name === 'branding');
            this.data.branding = branding;
          }
        });

        this.fetchJSON().then((json) => {
          json.tags = resolveTargets(json);
          this.data.json = json;
          this.resolveInitPromise(resolve);
        });

        this.fetchCaptions().then((res) => {
          this.data.json = { ...this.data.json, captions: res };
        });
      } else {
        this.resolveInitPromisePromise(resolve);
      }
    });
  }

  initializeAd(creativeData, videoString, target, adType, adData) {
    this.adType = adType;
    this.data = {};
    this.data.isMobileDevice = Helper.mobileCheck();
    this.data.isTabletDevice = Helper.tabletCheck();
    this.data.isFacebookApp = Helper.isFacebookApp();
    this.data.orientation = Helper.getOrientation();
    this.data.adNetwork = adData.network;
    this.data.tid = adData.tid;

    const urlMarcros =
      (creativeData && creativeData.prod && creativeData.prod.urlMacros) || [];
    const impScript =
      creativeData && creativeData.prod && creativeData.prod.impScript;
    if (impScript) {
      Helper.appendThirdPartyScriptToDOM(impScript);
    }
    this.data.eventMacros = {};
    urlMarcros.forEach((item) => {
      this.data.eventMacros[item.id] = item.macro;
    });

    if (!videoString) {
      videoString = creativeData && creativeData.id;
    }

    if (videoString) {
      this.data.bucketId = videoString.substr(0, 16);
      this.data.videoId = videoString.substr(16);
    } else {
      this.data.bucketId = config.DEFAULT_BUCKET_ID;
      this.data.videoId = config.DEFAULT_VIDEO_ID;
    }

    this.data.id = 'wtPlay_' + this.data.videoId;
    this.adUnit = {
      videoString: videoString,
      srv:
        (creativeData &&
          creativeData.srv &&
          Helper.trimLastSlash(creativeData.srv)) ||
        Helper.getDomainForAdUnit(target),
    };

    return new Promise((resolve, reject) => {
      this.fetchAdJSON(creativeData).then((json) => {
        json.tags = resolveTargets(json);
        this.data.json = json;
        this.resolveInitPromise(resolve, true);
      });
    });
  }
  // for MRAID project adunit only---
  fetchProjectVideoId(videoString, target) {
    const url = links.getProjectVideoId(
      Helper.getDomainForAdUnit(target),
      videoString
    );
    return Helper.request(url);
  }

  fetchJSON() {
    let queryParams = Helper.getUrlParams(window.location.href);
    let isPreviewLink = queryParams && queryParams.previewlink === '1';
    let isVideoDetails = queryParams && queryParams.edit === '1';
    let isTagEditor = queryParams && queryParams.editorMode === 'true';
    let url = links.getJSON(
      this.data.bucketId,
      this.data.videoId,
      isPreviewLink || isVideoDetails || isTagEditor
    );
    return Helper.request(url);
  }

  fetchCaptions() {
    try {
      let url = links.getCaptions(this.data.bucketId, this.data.videoId);
      return Helper.request(url);
    } catch (error) {
      return Promise.resolve([]);
    }
  }

  fetchAdJSON(creativeData) {
    let url = links.getAdJSON(
      this.adUnit.srv,
      this.data.bucketId,
      this.data.videoId
    );
    return Helper.request(url);
  }

  fetchUserJSON() {
    let url = links.getUserJSON(this.data.bucketId);
    return Helper.request(url);
  }

  fetchThirdPartyProductData(tagId, platform) {
    const args = {
      domain: this.adUnit && this.adUnit.srv,
      bucket: this.data.bucketId,
      videoId: this.data.videoId,
      tagId,
      platform,
    };
    let url = links.getThirdPartyProductData(args);
    return Helper.request(url);
  }

  setUser() {
    try {
      this.user = {
        id: this.viewer_id,
      };

      localStorage.setItem('user', JSON.stringify(this.user));
    } catch (e) {
      console.warn('localStorage is not present');
      this.user = {
        id: this.viewer_id,
      };
    }
  }

  getUser() {
    try {
      this.user =
        typeof localStorage !== 'undefined' && localStorage.getItem('user');
      this.user = JSON.parse(this.user);

      if (!this.user) {
        this.user = {
          id: this.viewer_id,
        };

        localStorage.setItem('user', JSON.stringify(this.user));
      }
    } catch (e) {
      console.warn('localStorage is not present');
      this.user = {
        id: this.viewer_id,
      };
    }
  }

  resolveInitPromise(resolve, isAd) {
    resolve({
      id: this.data.id,
      json: this.data.json,
      isMobileDevice: this.data.isMobileDevice && !this.data.isTabletDevice,
      isTabletDevice: this.data.isTabletDevice,
      isFacebookApp: this.data.isFacebookApp,
      orientation: this.data.orientation,
      branding: this.data.branding,
      adUnit: isAd ? this.adUnit : null,
    });
  }

  set(key, value) {
    return new Promise((resolve, reject) => {
      this.data[key] = value;
      resolve(this.data);
    });
  }

  setTags(data) {
    return new Promise((resolve, reject) => {
      if (this.data.json) {
        for (var key in data) {
          this.data.json[key] = data[key];
        }
      }

      resolve(this.data.json);
    });
  }

  getLogObject(log) {
    let timestamp = new Date().toISOString().split('T').join(' ');
    let getScreenRes = Helper.getScreenDimensions();
    let medium = Helper.getMedium();
    let origin = window.location.origin || '';
    if (!origin) {
      try {
        origin =
          window.parent &&
          window.parent.location &&
          window.parent.location.origin;
      } catch (err) {
        origin = '';
      }
    }

    let pathname = window.location.pathname;
    if (!pathname || pathname.includes('text/html;')) {
      try {
        const parent = window.parent;
        pathname =
          parent &&
          parent.location &&
          parent.location.pathname &&
          !parent.location.pathname.includes('text/html;') &&
          parent.location.pathname;
      } catch (error) {
        pathname = '';
      }
    }
    let event = {
      type: log.type,
      subtype: log.subtype,
      brand_id: this.data.json.brand_id,
      user_id: this.data.json.user_id,
      video_id: this.data.json.video_id,
      session_id: this.session_id,
      viewer_id: this.user && this.user.id,
      request_date: timestamp,
      requesttime: timestamp,
      host: `${origin}${pathname}`,
      screen_orientation: this.data.orientation,
      screen_resolution: `${getScreenRes}`,
      player_resolution: `${window.innerWidth}x${window.innerHeight}`,
      play_type: this.adType ? 'paid' : 'organic',
      medium: this.adType ? this.adType : medium,
      network: this.data.adNetwork || null,
      tid: this.data.tid || null,
      data_connection_type:
        (navigator.connection && navigator.connection.type) || 'NA',
      data_connection_effectivetype:
        (navigator.connection && navigator.connection.effectiveType) || 'NA',
      playback_duration: this.data.json.duration,
      video_type_id:
        this.data.json.tags && this.data.json.tags.length > 0 ? 1 : 2,
      // for interactive video `video_type_id` is 1 for non-interactive it is 2, it is used for utm prospects scoring---
      ...(this.data.eventMacros || {}),
    };

    // `variant_id` and `comparison_id` added in all events in case of AB Platform variants---
    const abVariantObj = {};
    const meta = this.data.json && this.data.json.meta;
    if (meta && meta.ab_comparison && meta.ab_variant) {
      abVariantObj.comparison_id = meta.ab_comparison;
      abVariantObj.variant_id = meta.ab_variant;
    }

    // additional third party tracker data like %%TTDDEVID%%
    const tpMacros = this.data.tpMacros || {};
    event = Object.assign({}, event, log, abVariantObj, tpMacros);

    if (log.current_duration) {
      event.current_duration = Helper.getReadableTime(
        Math.round(log.current_duration)
      );
    } else if (log.duration) {
      event.current_duration = Helper.getReadableTime(Math.round(log.duration));
    }

    delete event.type;
    delete event.subtype;
    delete event.sub_type;

    let params = new URLSearchParams(window.location.search);
    const utm_medium = params.get('utm_medium');
    const wt_utm_content = params.get('wt_utm_content');
    const utm_content = params.get('utm_content');

    const utmContentParam = wt_utm_content || utm_content;
    const utm =
      utm_medium || utmContentParam
        ? { utm_medium, utm_content: utmContentParam }
        : {};
    event = { ...event, ...utm };

    return Helper.parseJsonToFormData(event);
  }

  // logDataEvent(log) {
  //   let event = this.getLogObject(log);
  //   // let queryParams = Helper.getUrlParams(window.location.href);
  //   // let isPreviewLink = queryParams && queryParams.previewlink === '1';
  //   // let isVideoDetails = queryParams && queryParams.edit === '1';
  //   // let isTagEditor = queryParams && queryParams.editorMode === 'true';
  //   // const shouldFireEvent = isPreviewLink || isVideoDetails || isTagEditor;
  //   // console.log({ shouldFireEvent });
  //   // if (shouldFireEvent) return;

  //   try {
  //     let headers = new Headers();
  //     headers.set('Content-Type', 'application/x-www-form-urlencoded');

  //     Helper.request(links.sendLogs(this.adUnit && this.adUnit.srv), {
  //       method: 'POST',
  //       body: event,
  //       headers,
  //     });
  //   } catch (e) {}
  // }

  logDataEvent(log) {
    let event = this.getLogObject(log);
    if (this.viewer_id === constants.TEMP_VIEWER_ID) {
      this.eventsHeldForId.push(event);
    } else {
      this.fireEventApiCall(event);
    }
  }

  setThirdParyTrackerData = (macros = {}) => {
    this.data.tpMacros = macros;
  };

  fireEventApiCall(event) {
    try {
      let headers = new Headers();
      headers.set('Content-Type', 'application/x-www-form-urlencoded');

      Helper.request(links.sendLogs(this.adUnit && this.adUnit.srv), {
        method: 'POST',
        body: event,
        headers,
      });
    } catch (e) {}
  }

  logPollOption(log) {
    let event = this.getLogObject(log);

    try {
      let headers = new Headers();
      headers.set('Content-Type', 'application/x-www-form-urlencoded');

      return Helper.request(links.submitPoll(this.adUnit && this.adUnit.srv), {
        method: 'POST',
        body: event,
        headers,
      });
    } catch (e) {}
  }

  fetchRedeemCode(log) {
    let event = this.getLogObject(log);

    try {
      let headers = new Headers();
      headers.set('Content-Type', 'application/x-www-form-urlencoded');

      let domain =
        this.adUnit && this.adUnit.srv ? this.adUnit.srv : config.DOMAIN;
      let url = `${domain}/api/v1/getoffercode`;

      return Helper.request(url, {
        method: 'POST',
        body: event,
        headers,
      });
    } catch (e) {}
  }

  setTrackers(trackers) {
    return setTrackers(trackers);
  }

  trackEvent(trackers, logDataEvent, eventType, tag, eventDataObj) {
    let updatedData = Object.assign(eventDataObj, {
      video_title: this.data.json.title,
    });
    trackEvent(trackers, eventType, tag, updatedData, logDataEvent);
  }

  eventBus(action, data) {
    let eventArr = action.split('/');
    let logObject = this.delegateEvents(eventArr[0], action, data);

    this.logDataEvent(logObject);
  }

  delegateEvents(evt, action, data) {
    switch (evt) {
      case 'EVT':
        return dispatchEvent(action, data);
    }
  }
}



// WEBPACK FOOTER //
// ./components/store.js